import React, { useState } from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { type IdpPreset } from './idp-presets';

export interface CredentialsStepProps {
  /** The selected provider preset */
  readonly preset: IdpPreset;
  /** Template value (tenant ID, okta domain, etc.) — only used for providers with templates */
  readonly templateValue: string;
  /** Metadata URL (for custom providers or resolved from template) */
  readonly metadataUrl: string;
  /** Client ID value */
  readonly clientId: string;
  /** Client secret value */
  readonly clientSecret: string;
  /** Admin group ID — users in this group get admin role */
  readonly adminGroupId: string;
  /** Field-level validation errors */
  readonly errors: { metadataUrl?: string; clientId?: string; clientSecret?: string; templateValue?: string };
  /** Callback when a field value changes */
  readonly onChange: (field: string, value: string) => void;
}

/**
 * Step 2 of the IdP Configuration Wizard.
 * Shows provider-specific fields based on the selected preset:
 * - Entra ID: Tenant ID + Client ID + Client Secret
 * - Google: Client ID + Client Secret (metadata URL is fixed)
 * - Okta: Okta Domain + Client ID + Client Secret
 * - Custom: Full metadata URL + Client ID + Client Secret
 */
export function CredentialsStep({
  preset,
  templateValue,
  metadataUrl,
  clientId,
  clientSecret,
  adminGroupId,
  errors,
  onChange,
}: CredentialsStepProps): React.ReactElement {
  const [showSecret, setShowSecret] = useState(false);

  const isCustom = preset.id === 'custom-oidc' || preset.id === 'custom-saml';
  const hasTemplate = !!preset.metadataUrlTemplate;

  return (
    <div className="space-y-4">
      {/* Provider-specific template field (Tenant ID, Okta Domain, etc.) */}
      {hasTemplate && (
        <div className="space-y-2">
          <Label htmlFor="credentials-template-value">
            {preset.templatePlaceholderLabel}
          </Label>
          <p className="text-xs text-muted-foreground">
            {preset.templatePlaceholderHint}
          </p>
          {errors.templateValue && (
            <span className="text-sm text-destructive" role="alert">
              {errors.templateValue}
            </span>
          )}
          <Input
            id="credentials-template-value"
            type="text"
            value={templateValue}
            error={!!errors.templateValue}
            placeholder={preset.templatePlaceholderLabel}
            onChange={(e) => onChange('templateValue', e.target.value)}
          />
        </div>
      )}

      {/* Full metadata URL — only for custom providers */}
      {isCustom && (
        <div className="space-y-2">
          <Label htmlFor="credentials-metadata-url">
            {preset.protocol === 'oidc' ? 'OpenID Connect Discovery URL' : 'SAML Metadata XML URL'}
          </Label>
          <p className="text-xs text-muted-foreground">
            {preset.protocol === 'oidc'
              ? 'The .well-known/openid-configuration endpoint URL'
              : 'The URL to your SAML metadata XML document'}
          </p>
          {errors.metadataUrl && (
            <span className="text-sm text-destructive" id="credentials-metadata-url-error" role="alert">
              {errors.metadataUrl}
            </span>
          )}
          <Input
            id="credentials-metadata-url"
            type="url"
            maxLength={2048}
            value={metadataUrl}
            error={!!errors.metadataUrl}
            onChange={(e) => onChange('metadataUrl', e.target.value)}
            aria-describedby={errors.metadataUrl ? 'credentials-metadata-url-error' : undefined}
          />
        </div>
      )}

      {/* Resolved metadata URL display — for known providers */}
      {!isCustom && (
        <div className="rounded-md bg-muted p-3">
          <p className="text-xs font-medium text-muted-foreground">Discovery URL (auto-generated)</p>
          <code className="text-xs break-all">
            {metadataUrl || (preset.metadataUrlTemplate ?? preset.metadataUrl ?? '—')}
          </code>
        </div>
      )}

      {/* Client ID */}
      <div className="space-y-2">
        <Label htmlFor="credentials-client-id">
          Client ID
        </Label>
        <p className="text-xs text-muted-foreground">
          {preset.id === 'entra-id' && 'Application (client) ID from Azure App Registration'}
          {preset.id === 'google' && 'OAuth 2.0 Client ID from Google Cloud Console'}
          {preset.id === 'okta' && 'Client ID from your Okta application'}
          {isCustom && 'The OAuth2 client identifier'}
        </p>
        {errors.clientId && (
          <span className="text-sm text-destructive" id="credentials-client-id-error" role="alert">
            {errors.clientId}
          </span>
        )}
        <Input
          id="credentials-client-id"
          type="text"
          maxLength={256}
          value={clientId}
          error={!!errors.clientId}
          onChange={(e) => onChange('clientId', e.target.value)}
          aria-describedby={errors.clientId ? 'credentials-client-id-error' : undefined}
        />
      </div>

      {/* Client Secret */}
      <div className="space-y-2">
        <Label htmlFor="credentials-client-secret">
          Client Secret
        </Label>
        {errors.clientSecret && (
          <span className="text-sm text-destructive" id="credentials-client-secret-error" role="alert">
            {errors.clientSecret}
          </span>
        )}
        <div className="flex items-center gap-2">
          <Input
            id="credentials-client-secret"
            type={showSecret ? 'text' : 'password'}
            maxLength={512}
            value={clientSecret}
            error={!!errors.clientSecret}
            onChange={(e) => onChange('clientSecret', e.target.value)}
            aria-describedby={errors.clientSecret ? 'credentials-client-secret-error' : undefined}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowSecret((prev) => !prev)}
            aria-label={showSecret ? 'Hide client secret' : 'Show client secret'}
          >
            {showSecret ? 'Hide' : 'Show'}
          </Button>
        </div>
      </div>

      {/* Admin Group ID */}
      <div className="space-y-2">
        <Label htmlFor="credentials-admin-group">
          Admin Group ID <span className="text-muted-foreground font-normal">(optional)</span>
        </Label>
        <p className="text-xs text-muted-foreground">
          {preset.id === 'entra-id' && 'The Object ID of the Entra ID group whose members should be admins'}
          {preset.id === 'google' && 'The Google Workspace group email (e.g., admins@yourdomain.com)'}
          {preset.id === 'okta' && 'The Okta group name or ID for admin users'}
          {(preset.id === 'custom-oidc' || preset.id === 'custom-saml') && 'The group claim value that grants admin role'}
        </p>
        <Input
          id="credentials-admin-group"
          type="text"
          maxLength={256}
          value={adminGroupId}
          placeholder="e.g., CoreFlow-Admins"
          onChange={(e) => onChange('adminGroupId', e.target.value)}
        />
      </div>
    </div>
  );
}
